import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getQuizById,
  startQuizAttempt,
  answerQuestion,
  readyForQuestion,
  getAttemptDetails,
  getMyQuizAttempts,
  getQuizVideoProgress,
  updateQuizVideoProgress,
  getRetakeInfo,
  QuizWithQuestions,
  QuizAttempt,
  QuizQuestion,
  AnswerResponse,
  QuizVideoProgress,
  RetakeInfo,
  ShuffledAttemptQuestion,
  getMyLives,
  getMyCategoryQuizzes,
  type LivesState,
} from "../../services/quizzes";
import { getLivesPurchaseConfig } from "../../services/livesPurchase";
import { hapticCorrect, hapticWrong, hapticCelebrate } from "../../utils/haptics";
import { fireConfetti } from "../../utils/confetti";
import { LivesBadge, LevelBar, RingProgress, GameCard, GameButton, GameIcon } from "../../components/game";
import { getAthleteThisWeekDrops } from "../../services/quizSchedules";
import { getYouTubeVideoId, YTPlayer } from "./quiz/youtube";
import {
  initialVideoSaveState,
  isVideoWatched,
  markVideoProgressSent,
  shouldSendVideoProgress,
  VideoSaveState,
} from "../../lib/videoProgressThrottle";
import { resolveMediaUrl } from "./quiz/media";
import QuizHeader from "./quiz/QuizHeader";
import CircularTimer from "./quiz/CircularTimer";
import { canSubmitMulti, canConfirmTrueFalse } from "../../lib/quizAnswers";

type QuizPhase = "intro" | "video" | "questions" | "result";

export default function QuizTaking() {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  // Where the athlete came from (e.g. a category screen). Set as navigation
  // state by CategoryQuizzes so the header back button returns there instead
  // of always dumping the athlete on the top-level Quizzes list.
  const backTarget = (location.state as { from?: string } | null)?.from || "/app/quizzes";

  // Core state
  const [quiz, setQuiz] = useState<QuizWithQuestions | null>(null);
  const [attempt, setAttempt] = useState<QuizAttempt | null>(null);
  const [phase, setPhase] = useState<QuizPhase>("intro");
  // Message shown in the intro when starting is blocked server-side (e.g. a
  // themed quiz whose earlier quizzes aren't finished yet).
  const [startError, setStartError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [previousAttempts, setPreviousAttempts] = useState<QuizAttempt[]>([]);
  const [retakeInfo, setRetakeInfo] = useState<RetakeInfo | null>(null);
  const [showRetakeConfirm, setShowRetakeConfirm] = useState(false);

  // Video state
  const [_videoProgress, setVideoProgress] = useState<QuizVideoProgress | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [videoWatchPercent, setVideoWatchPercent] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  // Ref on the YouTube wrapper (not the iframe) so we can request
  // fullscreen from a user tap without hitting the "strip
  // allowfullscreen" restriction on the iframe itself. The
  // fullscreenchange guard elsewhere only bounces IFRAME fullscreens,
  // so entering fullscreen on this DIV is allowed.
  const ytWrapperRef = useRef<HTMLDivElement>(null);

  const requestFullscreen = (target: HTMLElement | null) => {
    if (!target) return;
    // iOS Safari native <video>: prefer the webkit-specific method,
    // which routes through iOS's native video player and works when
    // Fullscreen API is otherwise blocked in the WebView.
    const nativeVideo = target instanceof HTMLVideoElement ? target : null;
    if (nativeVideo && typeof (nativeVideo as any).webkitEnterFullscreen === "function") {
      try {
        (nativeVideo as any).webkitEnterFullscreen();
        return;
      } catch {
        // fall through to the standard API
      }
    }
    const req =
      target.requestFullscreen ||
      (target as any).webkitRequestFullscreen ||
      (target as any).mozRequestFullScreen ||
      (target as any).msRequestFullscreen;
    if (req) {
      try {
        req.call(target);
      } catch {
        // Some browsers (rare) reject if the gesture isn't a "user
        // activation" event. Silent no-op — the user just taps again.
      }
    }
  };

  const exitFullscreen = () => {
    const exit =
      document.exitFullscreen ||
      (document as any).webkitExitFullscreen ||
      (document as any).mozCancelFullScreen ||
      (document as any).msExitFullscreen;
    if (exit) {
      try {
        exit.call(document);
      } catch {
        /* silent */
      }
    }
  };

  // Toggle state for the fullscreen button icon (enter vs exit) and
  // for pinning ScreenOrientation while the video is enlarged.
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);

  // YouTube player state
  const [ytPlayer, setYtPlayer] = useState<YTPlayer | null>(null);
  const [ytPlaying, setYtPlaying] = useState(false);
  const [ytDuration, setYtDuration] = useState(0);
  const [ytCurrentTime, setYtCurrentTime] = useState(0);
  const [ytReady, setYtReady] = useState(false);
  const [ytApiLoaded, setYtApiLoaded] = useState(false);
  const [ytError, setYtError] = useState(false);
  const ytProgressInterval = useRef<NodeJS.Timeout | null>(null);
  const ytPlayerRef = useRef<YTPlayer | null>(null);
  const currentYouTubeVideoId = useRef<string | null>(null);

  // Refs for stable callbacks (avoid stale closures)
  const quizIdRef = useRef(quizId);
  const quizRef = useRef(quiz);
  const currentChapterIndexRef = useRef(currentChapterIndex);
  const lastAnswerResponseRef = useRef<AnswerResponse | null>(null);
  // What the last video-progress save sent, and the latest known playback
  // position (used for a best-effort save when the athlete leaves).
  const videoSaveRef = useRef<VideoSaveState>(initialVideoSaveState());
  const lastVideoPlaybackRef = useRef({ position: 0, duration: 0 });

  // Keep refs in sync
  useEffect(() => {
    quizIdRef.current = quizId;
    quizRef.current = quiz;
    currentChapterIndexRef.current = currentChapterIndex;
  }, [quizId, quiz, currentChapterIndex]);

  // Question state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<any>(null);
  const [feedback, setFeedback] = useState<AnswerResponse | null>(null);
  const [showingFeedback, setShowingFeedback] = useState(false);
  const [answering, setAnswering] = useState(false);

  // F1: shuffled-question flow. `shuffledQuestions` comes back from
  // POST /attempts with options already permuted; we trust it as the
  // source of truth for what the athlete sees, and send back the
  // POSITION they clicked (server de-shuffles). `quizMeta` carries
  // the quiz-level timer + preview_seconds so we can size the two
  // sub-phases correctly.
  const [shuffledQuestions, setShuffledQuestions] = useState<ShuffledAttemptQuestion[]>([]);
  const [quizMeta, setQuizMeta] = useState<{ time_limit_seconds: number | null; preview_seconds: number | null } | null>(null);
  // "preview" = question text alone with a "Am înțeles" button
  // "answer"  = options + timer, points scale with speed
  const [questionSubPhase, setQuestionSubPhase] = useState<"preview" | "answer">("preview");
  // Preview countdown (ms) — auto-advances to answer sub-phase at 0.
  const [previewMsLeft, setPreviewMsLeft] = useState<number>(0);
  // Timer state while in answer sub-phase.
  const [timerMs, setTimerMs] = useState<number>(0);       // total budget for this question
  const [timerMsLeft, setTimerMsLeft] = useState<number>(0); // remaining
  // Position (index in the shuffled options array) the athlete clicked.
  // Kept separate from selectedAnswer so single/multi flow can send
  // `selected_position` (position) while true_false still sends `answer`.
  const [selectedPosition, setSelectedPosition] = useState<number | null>(null);
  // Multi-choice: the set of positions (in the shuffled array) the
  // athlete has checked. Sent as `selectedPositions` on submit.
  const [selectedPositionsMulti, setSelectedPositionsMulti] = useState<number[]>([]);
  // F3: badges the athlete just earned on this completion — rendered
  // as a celebration card on the result screen.
  const [earnedBadges, setEarnedBadges] = useState<NonNullable<AnswerResponse["newly_earned_badges"]>>([]);
  // Sprint 1 — live consecutive-correct streak shown during the quiz,
  // full-screen badge-unlock celebration, and refs so feedback can be
  // skipped (tap to continue) instead of waiting out the fixed delay.
  const [answerStreak, setAnswerStreak] = useState(0);
  const [showBadgeCelebration, setShowBadgeCelebration] = useState(false);
  // A suggested next quiz shown on the result screen (first uncompleted
  // scheduled drop that isn't this quiz). Null = nothing to suggest.
  const [nextQuiz, setNextQuiz] = useState<{ id: string; title: string; topic: string; reward_points: number; max_points?: number; plan_locked?: boolean } | null>(null);
  // Lives — a global regenerating pool (shared across quizzes). `lives` is
  // the current heart count in-attempt; `livesInfo` carries the pool max and
  // the regen countdown for the intro screen. Kept in sync with the server.
  const [lives, setLives] = useState(3);
  const [livesInfo, setLivesInfo] = useState<LivesState | null>(null);
  // Whether the athlete can buy lives (purchases enabled + they're an
  // eligible buyer for their own account) — gates the "buy lives" CTA.
  const [canBuyLives, setCanBuyLives] = useState(false);
  // Ticks every second so the "next life in mm:ss" countdown updates live.
  const [nowTs, setNowTs] = useState(() => Date.now());
  const livesFetchedAtRef = useRef(0);
  const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const advanceRef = useRef<(() => void) | null>(null);

  // Global lives pool — fetch for the intro gate and tick a countdown.
  const refreshLives = useCallback(async () => {
    try {
      const s = await getMyLives();
      setLivesInfo(s);
      livesFetchedAtRef.current = Date.now();
      setNowTs(Date.now());
    } catch {
      /* ignore — lives are best-effort UI */
    }
  }, []);
  useEffect(() => {
    refreshLives();
  }, [refreshLives]);
  // Can this athlete buy lives for themselves? (adults only, purchases on)
  useEffect(() => {
    (async () => {
      try {
        const cfg = await getLivesPurchaseConfig();
        setCanBuyLives(cfg.enabled && cfg.athletes.some((a) => a.is_self));
      } catch {
        /* ignore — CTA just stays hidden */
      }
    })();
  }, []);
  // 1s ticker while on the intro screen so the countdown updates.
  useEffect(() => {
    if (phase !== "intro") return;
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);
  // When the countdown to the next life elapses, refetch the real pool.
  useEffect(() => {
    if (!livesInfo || livesInfo.lives >= livesInfo.max) return;
    const msToNext = livesInfo.ms_to_next_life - (nowTs - livesFetchedAtRef.current);
    if (msToNext <= 0) refreshLives();
  }, [nowTs, livesInfo, refreshLives]);

  // Progress tracking
  const [progress, setProgress] = useState({
    answered: 0,
    total: 0,
    correct_count: 0,
    wrong_count: 0,
    total_score: 0,
  });

  // Result state
  const [finalResult, setFinalResult] = useState<{
    score: number;
    max_score: number;
    correct_count: number;
    wrong_count: number;
    points_earned: number;
    points_awarded?: number;
    points_deducted?: number;
    net_points?: number;
    is_perfect_score?: boolean;
    perfect_bonus?: number;
    base_from_correct?: number;
    speed_bonus?: number;
    is_retake?: boolean;
    previous_best?: number;
    out_of_lives?: boolean;
    level_after?: AnswerResponse["level_after"];
    leveled_up?: boolean;
  } | null>(null);

  useEffect(() => {
    if (quizId) {
      // Reset transient run state so navigating straight from one quiz's
      // result screen into the next (via the suggestion card) starts clean.
      setPhase("intro");
      setFinalResult(null);
      setEarnedBadges([]);
      setShowBadgeCelebration(false);
      setAttempt(null);
      setShuffledQuestions([]);
      setCurrentQuestionIndex(0);
      setQuestionSubPhase("preview");
      setLives(3);
      setAnswerStreak(0);
      setNextQuiz(null);
      loadQuiz();
    }
  }, [quizId]);

  // When the athlete reaches the result screen, suggest the NEXT quiz in the
  // same category, in the admin-configured order — so finishing one quiz leads
  // straight into the next step of the learning path. We reuse the category
  // endpoint (theme_order asc), take the quiz right after the one just
  // finished, skipping empties and any still theme-locked further ahead. A
  // plan-locked next quiz is still surfaced but flagged so the button routes a
  // Free athlete to the upgrade screen instead of starting a paid quiz.
  //
  // Falls back to the first uncompleted weekly drop when we can't resolve the
  // category (e.g. the athlete arrived from somewhere other than a category
  // screen). Best-effort — silent on failure.
  useEffect(() => {
    if (phase !== "result") return;
    let cancelled = false;
    // backTarget looks like "/app/quizzes/category/<name>" when the athlete
    // came from a category screen.
    const m = backTarget.match(/\/app\/quizzes\/category\/([^/?]+)/);
    const categoryName = m ? decodeURIComponent(m[1]) : null;
    (async () => {
      try {
        if (categoryName) {
          const resp = await getMyCategoryQuizzes(categoryName);
          const list = resp?.quizzes || [];
          const idx = list.findIndex((q) => q.id === quizId);
          if (idx >= 0) {
            // First quiz after the current one that is takeable (has questions
            // and isn't theme-locked) OR is plan-locked (offer upgrade). Skip
            // theme-locked/empty ones entirely.
            const cand = list
              .slice(idx + 1)
              .find((q) => q.plan_locked || (!q.locked && q.question_count > 0));
            if (!cancelled && cand) {
              setNextQuiz({
                id: cand.id,
                title: cand.title,
                topic: resp.category.title,
                reward_points: cand.reward_points,
                max_points: cand.max_points,
                plan_locked: !!cand.plan_locked,
              });
              return;
            }
            if (!cancelled) return; // resolved category, nothing suitable ahead
          }
        }
        // Fallback: first uncompleted scheduled drop for this week.
        const resp = await getAthleteThisWeekDrops();
        const cand = (resp?.drops || []).find(
          (d) => d.quiz.id !== quizId && d.status !== "completed"
        );
        if (!cancelled && cand) {
          setNextQuiz({
            id: cand.quiz.id,
            title: cand.quiz.title,
            topic: cand.quiz.topic,
            reward_points: cand.quiz.reward_points,
            max_points: cand.quiz.max_points,
          });
        }
      } catch {
        /* suggestion is optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, quizId, backTarget]);

  // Sends the watch progress to the server. Regular ticks are throttled by
  // time; `force` (pause, ended, leaving) skips the wait. Completion is sent
  // the moment the video passes 90%, so the Cinefil badge counts it.
  const saveVideoProgress = useCallback(
    (position: number, duration: number, force = false) => {
      if (!Number.isFinite(position) || !Number.isFinite(duration) || duration <= 0) return;
      lastVideoPlaybackRef.current = { position, duration };

      const q = quizRef.current;
      const qId = quizIdRef.current;
      if (!q || !qId) return;

      const completed = isVideoWatched(position, duration);
      const now = Date.now();
      const previous = videoSaveRef.current;
      if (!shouldSendVideoProgress(previous, { now, position, completed, force })) return;

      const sent = markVideoProgressSent(now, position, completed, previous);
      videoSaveRef.current = sent;
      updateQuizVideoProgress(qId, {
        watch_progress: Math.floor(position),
        chapter_index: q.video_mode === "chapters" ? currentChapterIndexRef.current : undefined,
        completed,
      }).catch(() => {
        // Undo, so the next tick retries (a lost "completed" must not stick).
        if (videoSaveRef.current === sent) videoSaveRef.current = previous;
      });
    },
    [],
  );

  // Stable progress update function using refs
  const updateYouTubeProgress = useCallback((currentTime: number, duration: number) => {
    if (!duration || duration <= 0) return;

    const percent = Math.min(100, Math.round((currentTime / duration) * 100));
    setYtCurrentTime(currentTime);
    setYtDuration(duration);
    setVideoWatchPercent(percent);

    // Check for 90% completion
    if (percent >= 90 && !videoCompleted) {
      setVideoCompleted(true);
    }

    saveVideoProgress(currentTime, duration);
  }, [videoCompleted, saveVideoProgress]);

  // Best-effort save when the athlete leaves the video (phase change or
  // leaving the page). Does nothing if the position was already sent.
  useEffect(() => {
    return () => {
      const { position, duration } = lastVideoPlaybackRef.current;
      saveVideoProgress(position, duration, true);
    };
  }, [phase, saveVideoProgress]);

  // Load YouTube IFrame API
  useEffect(() => {
    // Only load if we're in video phase with a YouTube video
    if (phase !== "video" || !quiz?.video_url) return;

    const videoId = getYouTubeVideoId(quiz.video_url);
    if (!videoId) return;

    // Reset YouTube state when entering video phase
    setYtReady(false);
    setYtError(false);
    setYtPlaying(false);

    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      setYtApiLoaded(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    if (existingScript) {
      // Wait for it to load
      const checkInterval = setInterval(() => {
        if (window.YT && window.YT.Player) {
          setYtApiLoaded(true);
          clearInterval(checkInterval);
        }
      }, 100);

      // Timeout after 10 seconds
      const timeout = setTimeout(() => {
        clearInterval(checkInterval);
        setYtError(true);
        console.error("YouTube API failed to load within 10 seconds");
      }, 10000);

      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeout);
      };
    }

    // Load the YouTube IFrame API script
    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;

    script.onerror = () => {
      setYtError(true);
      console.error("Failed to load YouTube IFrame API script");
    };

    window.onYouTubeIframeAPIReady = () => {
      console.log("YouTube IFrame API Ready");
      setYtApiLoaded(true);
    };

    document.body.appendChild(script);

    return () => {
      window.onYouTubeIframeAPIReady = undefined;
    };
  }, [phase, quiz?.video_url]);

  // Track fullscreen state so the button icon can flip between
  // "enter" and "exit" and so we can pin the screen orientation
  // only while the video is actually enlarged. Same listener also
  // lets us unlock rotation on enter (so the user can turn the
  // phone sideways) and re-lock to portrait on exit.
  useEffect(() => {
    if (phase !== "video") return;
    let unlockedForVideo = false;
    const onChange = async () => {
      const el = document.fullscreenElement || (document as any).webkitFullscreenElement;
      // Only count DIV/VIDEO fullscreens as "our" video fullscreen.
      // The other listener nearby bounces IFRAME fullscreens (YouTube
      // end-card auto-triggers) and their fullscreenchange events
      // shouldn't flip our button icon.
      const ourFs = !!el && el.tagName !== "IFRAME";
      setIsVideoFullscreen(ourFs);
      if (ourFs && !unlockedForVideo) {
        try {
          const { ScreenOrientation } = await import("@capacitor/screen-orientation");
          await ScreenOrientation.unlock();
          unlockedForVideo = true;
        } catch {
          // Plugin unavailable on web / desktop — ignore.
        }
      } else if (!ourFs && unlockedForVideo) {
        try {
          const { ScreenOrientation } = await import("@capacitor/screen-orientation");
          await ScreenOrientation.lock({ orientation: "portrait" });
        } catch {
          // ignore
        }
        unlockedForVideo = false;
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
      // Belt-and-suspenders: if the athlete navigates away mid-FS
      // (back button, phase change), make sure we don't leave the
      // rest of the app rotatable.
      if (unlockedForVideo) {
        import("@capacitor/screen-orientation")
          .then(({ ScreenOrientation }) => ScreenOrientation.lock({ orientation: "portrait" }))
          .catch(() => {});
      }
    };
  }, [phase]);

  // Phase-exit cleanup for the YouTube iframe.
  //
  // YT.Player() *replaces* our <div id="yt-player-container"> with an
  // <iframe id="yt-player-container"> in the DOM. React's reconciliation
  // still holds a reference to the original div node, so when we move
  // off the "video" phase and React unmounts the video subtree, the
  // removal call no-ops (the div React tracked no longer exists; the
  // iframe in its place isn't part of the React tree). The orphan
  // iframe then gets adopted by whatever DOM node lands at the same
  // position next — in our case the question-header flex div — and
  // covers the questions because of its `absolute inset-0`.
  //
  // Calling player.destroy() detaches the iframe cleanly. We also scan
  // for any leftover #yt-player-container iframe (defensive — covers
  // the case where destroy() throws because the player was mid-init).
  useEffect(() => {
    if (phase === "video") return;
    if (ytPlayerRef.current) {
      try {
        ytPlayerRef.current.destroy();
      } catch {
        // ignore — fallback removal below
      }
      ytPlayerRef.current = null;
    }
    setYtPlayer(null);
    setYtReady(false);
    setYtPlaying(false);
    document
      .querySelectorAll<HTMLIFrameElement>("iframe#yt-player-container")
      .forEach((el) => el.remove());
  }, [phase]);

  // Belt-and-suspenders: even after we strip `allowfullscreen` on the
  // iframe, some browsers (notably iOS Safari) still let YouTube's
  // end-card UI request fullscreen via the JS Fullscreen API. Watch
  // the document for any FS transition during the video phase and
  // bounce it back to inline so the iframe stays trapped inside the
  // card and the "Continue" button + progress stay reachable.
  useEffect(() => {
    if (phase !== "video") return;
    const onFsChange = () => {
      const el = document.fullscreenElement || (document as any).webkitFullscreenElement;
      if (el && el.tagName === "IFRAME") {
        try {
          (document.exitFullscreen || (document as any).webkitExitFullscreen)?.call(document);
        } catch {
          // Some browsers reject programmatic exit if it wasn't user-initiated.
          // Worst case the user taps once to dismiss; we still prevented the
          // overlay-over-questions issue at end-of-video.
        }
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, [phase]);

  // Initialize YouTube player when API is loaded
  useEffect(() => {
    if (!ytApiLoaded || phase !== "video" || !quiz?.video_url || ytError) return;

    const videoId = getYouTubeVideoId(quiz.video_url);
    if (!videoId) return;

    // Don't recreate player if same video and already ready
    if (ytPlayerRef.current && currentYouTubeVideoId.current === videoId && ytReady) {
      return;
    }

    // Destroy existing player
    if (ytPlayerRef.current) {
      try {
        ytPlayerRef.current.destroy();
      } catch (e) {
        console.warn("Error destroying YouTube player:", e);
      }
      ytPlayerRef.current = null;
      setYtPlayer(null);
      setYtReady(false);
    }

    currentYouTubeVideoId.current = videoId;

    // Wait for container to be in DOM
    const containerId = "yt-player-container";
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max

    const createPlayer = () => {
      attempts++;
      const container = document.getElementById(containerId);

      if (!container) {
        if (attempts < maxAttempts) {
          setTimeout(createPlayer, 100);
        } else {
          console.error("YouTube player container not found after 5 seconds");
          setYtError(true);
        }
        return;
      }

      console.log("Creating YouTube player for video:", videoId);

      try {
        const player = new window.YT.Player(containerId, {
          videoId: videoId,
          playerVars: {
            controls: 0, // Hide all controls
            disablekb: 1, // Disable keyboard controls
            fs: 0, // Disable fullscreen button
            iv_load_policy: 3, // Hide annotations
            modestbranding: 1, // Minimal branding
            rel: 0, // Don't show related videos
            showinfo: 0, // Hide video info (deprecated but kept for older players)
            playsinline: 1, // Play inline on mobile
            enablejsapi: 1, // Enable JS API
            origin: window.location.origin,
          },
          events: {
            onReady: (event: { target: YTPlayer }) => {
              console.log("YouTube player ready, duration:", event.target.getDuration());
              ytPlayerRef.current = event.target;
              setYtPlayer(event.target);
              setYtDuration(event.target.getDuration() || 0);
              setYtReady(true);
              // Strip fullscreen capability from the freshly-injected
              // iframe. YT.PlayerVars.fs only hides the FS button — the
              // iframe still has `allowfullscreen` and YouTube's own
              // gesture handlers can elevate it via the browser's
              // Fullscreen API at end-of-video, leaving the questions
              // covered. Removing the attribute denies the gesture
              // outright. Done in onReady so we know the iframe exists.
              try {
                const iframe = document
                  .getElementById("yt-player-container")
                  ?.querySelector("iframe");
                if (iframe) {
                  iframe.removeAttribute("allowfullscreen");
                  iframe.removeAttribute("webkitallowfullscreen");
                  iframe.removeAttribute("mozallowfullscreen");
                  iframe.setAttribute("allow", "autoplay; encrypted-media");
                }
              } catch (e) {
                // Defensive — iframe may already be unmounted if the
                // user navigated away. Nothing to clean up.
              }
            },
            onStateChange: (event: { data: number; target: YTPlayer }) => {
              console.log("YouTube state change:", event.data);
              // YT.PlayerState: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (video cued)
              if (event.data === 1) {
                // Playing
                setYtPlaying(true);
                // Clear any existing interval
                if (ytProgressInterval.current) {
                  clearInterval(ytProgressInterval.current);
                }
                // Start tracking progress every 500ms for smoother updates
                ytProgressInterval.current = setInterval(() => {
                  try {
                    const currentTime = event.target.getCurrentTime();
                    const duration = event.target.getDuration();
                    if (duration > 0) {
                      updateYouTubeProgress(currentTime, duration);
                    }
                  } catch (e) {
                    console.warn("Error getting YouTube time:", e);
                  }
                }, 500);
              } else if (event.data === 2) {
                // Paused
                setYtPlaying(false);
                try {
                  saveVideoProgress(event.target.getCurrentTime(), event.target.getDuration(), true);
                } catch (e) {
                  // Player may be gone; the periodic save covers it.
                }
                if (ytProgressInterval.current) {
                  clearInterval(ytProgressInterval.current);
                  ytProgressInterval.current = null;
                }
              } else if (event.data === 0) {
                // Ended
                setYtPlaying(false);
                setVideoWatchPercent(100);
                setVideoCompleted(true);
                try {
                  const endDuration = event.target.getDuration();
                  saveVideoProgress(endDuration, endDuration, true);
                } catch (e) {
                  // Player may be gone.
                }
                if (ytProgressInterval.current) {
                  clearInterval(ytProgressInterval.current);
                  ytProgressInterval.current = null;
                }
              }
            },
            onError: (event: { data: number }) => {
              console.error("YouTube player error:", event.data);
              setYtError(true);
            },
          },
        });
      } catch (e) {
        console.error("Error creating YouTube player:", e);
        setYtError(true);
      }
    };

    // Small delay to ensure DOM is ready
    setTimeout(createPlayer, 200);

    return () => {
      if (ytProgressInterval.current) {
        clearInterval(ytProgressInterval.current);
        ytProgressInterval.current = null;
      }
    };
  }, [ytApiLoaded, phase, quiz?.video_url, ytError, updateYouTubeProgress, saveVideoProgress]);

  // Cleanup YouTube player on phase change or unmount
  useEffect(() => {
    return () => {
      if (ytPlayerRef.current) {
        try {
          ytPlayerRef.current.destroy();
        } catch (e) {
          // Ignore
        }
        ytPlayerRef.current = null;
      }
      if (ytProgressInterval.current) {
        clearInterval(ytProgressInterval.current);
        ytProgressInterval.current = null;
      }
    };
  }, []);

  const loadQuiz = async () => {
    try {
      setLoading(true);
      videoSaveRef.current = initialVideoSaveState();
      lastVideoPlaybackRef.current = { position: 0, duration: 0 };
      const [quizData, attemptsData, retakeData] = await Promise.all([
        getQuizById(quizId!),
        getMyQuizAttempts(quizId!),
        getRetakeInfo(quizId!).catch(() => null), // Retake info might fail for new quizzes
      ]);

      setQuiz(quizData);
      setPreviousAttempts(attemptsData);
      setRetakeInfo(retakeData);
      setProgress((prev) => ({ ...prev, total: quizData.questions.length }));

      // Load video progress if quiz has video
      if (quizData.video_mode && quizData.video_mode !== "none") {
        try {
          const vProgress = await getQuizVideoProgress(quizId!);
          setVideoProgress(vProgress);
          setVideoCompleted(vProgress.completed);
          if (quizData.video_mode === "chapters" && vProgress.chapter_index !== undefined) {
            setCurrentChapterIndex(vProgress.chapter_index);
          }
        } catch (e) {
          // No progress yet, that's fine
        }
      }
    } catch (error) {
      console.error("Failed to load quiz:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = async () => {
    if (!quiz) return;

    // A quiz with no questions can't be taken — surface a message instead of
    // starting an empty attempt.
    if (!quiz.questions || quiz.questions.length === 0) {
      setStartError(t("quiz.noQuestionsYet", "Acest quiz nu conține încă întrebări."));
      return;
    }

    // If it's a retake and user hasn't confirmed, show confirmation modal
    if (retakeInfo?.is_retake && retakeInfo.retake_deduction > 0 && !showRetakeConfirm) {
      setShowRetakeConfirm(true);
      return;
    }

    // Reset confirmation state
    setShowRetakeConfirm(false);

    try {
      const newAttempt = await startQuizAttempt(quizId!);
      setAttempt(newAttempt);
      setShuffledQuestions(newAttempt.questions || []);
      setQuizMeta(newAttempt.quiz_meta || null);
      setCurrentQuestionIndex(0);
      setQuestionSubPhase("preview");
      setProgress({
        answered: 0,
        total: quiz.questions.length,
        correct_count: 0,
        wrong_count: 0,
        total_score: 0,
      });
      setAnswerStreak(0);
      setLives(livesInfo?.lives ?? 5);
      setSelectedPositionsMulti([]);
      setEarnedBadges([]);
      setShowBadgeCelebration(false);

      // Determine next phase based on video mode
      // Treat null/undefined video_mode as "none"
      const hasVideo = quiz.video_mode && quiz.video_mode !== "none" && quiz.video_url;
      if (!hasVideo || videoCompleted) {
        setPhase("questions");
      } else {
        setPhase("video");
      }
    } catch (error: any) {
      console.error("Failed to start quiz:", error);
      // Out of lives — surface the pool + countdown so the intro can gate.
      if (error?.response?.data?.code === "OUT_OF_LIVES") {
        if (error.response.data.lives) {
          setLivesInfo(error.response.data.lives);
          livesFetchedAtRef.current = Date.now();
          setNowTs(Date.now());
        } else {
          refreshLives();
        }
      } else if (error?.response?.data?.code === "PREREQUISITE_NOT_MET") {
        // Themed quiz locked behind an earlier one — show the server message.
        setStartError(
          error.response.data.error ||
            t("quiz.prerequisiteLocked", "Trebuie să finalizezi întâi quiz-ul anterior din temă.")
        );
      }
    }
  };

  const handleConfirmRetake = async () => {
    // User confirmed retake, now start the quiz
    setShowRetakeConfirm(false);
    if (!quiz) return;

    try {
      const newAttempt = await startQuizAttempt(quizId!);
      setAttempt(newAttempt);
      setShuffledQuestions(newAttempt.questions || []);
      setQuizMeta(newAttempt.quiz_meta || null);
      setCurrentQuestionIndex(0);
      setQuestionSubPhase("preview");
      setProgress({
        answered: 0,
        total: quiz.questions.length,
        correct_count: 0,
        wrong_count: 0,
        total_score: 0,
      });
      setAnswerStreak(0);
      setLives(livesInfo?.lives ?? 5);
      setSelectedPositionsMulti([]);
      setEarnedBadges([]);
      setShowBadgeCelebration(false);

      // Determine next phase based on video mode
      // Treat null/undefined video_mode as "none"
      const hasVideo = quiz.video_mode && quiz.video_mode !== "none" && quiz.video_url;
      if (!hasVideo || videoCompleted) {
        setPhase("questions");
      } else {
        setPhase("video");
      }
    } catch (error: any) {
      console.error("Failed to start quiz:", error);
      // Out of lives — surface the pool + countdown so the intro can gate.
      if (error?.response?.data?.code === "OUT_OF_LIVES") {
        if (error.response.data.lives) {
          setLivesInfo(error.response.data.lives);
          livesFetchedAtRef.current = Date.now();
          setNowTs(Date.now());
        } else {
          refreshLives();
        }
      } else if (error?.response?.data?.code === "PREREQUISITE_NOT_MET") {
        // Themed quiz locked behind an earlier one — show the server message.
        setStartError(
          error.response.data.error ||
            t("quiz.prerequisiteLocked", "Trebuie să finalizezi întâi quiz-ul anterior din temă.")
        );
      }
    }
  };

  const handleVideoProgress = (currentTime: number, duration: number) => {
    if (!quiz || !quizId || !duration || !Number.isFinite(duration)) return;

    // Update watch percentage
    const percent = Math.min(100, Math.round((currentTime / duration) * 100));
    setVideoWatchPercent(percent);

    // Update local state
    if (isVideoWatched(currentTime, duration) && !videoCompleted) {
      setVideoCompleted(true);
    }

    // Throttled save to the server (completion goes out at once).
    saveVideoProgress(currentTime, duration);
  };

  const handleChapterComplete = () => {
    if (!quiz || !quiz.video_chapters) return;

    // Move to questions for this chapter
    setPhase("questions");
  };

  const handleVideoComplete = () => {
    if (!quiz) return;

    if (quiz.video_mode === "full") {
      setVideoCompleted(true);
      setPhase("questions");
    } else if (quiz.video_mode === "chapters") {
      handleChapterComplete();
    }
  };

  const getCurrentChapterQuestions = (): QuizQuestion[] => {
    if (!quiz || quiz.video_mode !== "chapters") {
      return quiz?.questions || [];
    }

    // Prefer the backend's shuffled set (options permuted fresh on every
    // start/retake) so athletes can't memorise answer positions. Chapter
    // mode used to filter quiz.questions (original, un-shuffled order),
    // which bypassed the shuffle. Fall back to raw questions only if we're
    // here without a fresh attempt.
    const source =
      shuffledQuestions.length > 0
        ? (shuffledQuestions as unknown as QuizQuestion[])
        : quiz.questions;
    return source.filter((q) => q.chapter_index === currentChapterIndex);
  };

  const getQuestionsForPhase = (): QuizQuestion[] => {
    if (!quiz) return [];

    if (quiz.video_mode === "chapters") {
      return getCurrentChapterQuestions();
    }

    // Prefer the shuffled+expanded set the backend returns from
    // POST /attempts — options are already permuted for display, and
    // per-question metadata (auto_preview_ms) rides along. Fall back
    // to quiz.questions only if we somehow entered this phase without
    // a fresh attempt (edge case; keeps the UI from crashing).
    if (shuffledQuestions.length > 0) {
      return shuffledQuestions as unknown as QuizQuestion[];
    }
    return quiz.questions;
  };

  const handleAnswerSelect = (answer: any) => {
    if (showingFeedback || answering) return;
    // true/false: a tap only selects (and can be changed); the answer is
    // sent by the "Confirmă" button, like multi-choice. The server times the
    // answer when it arrives, so the speed bonus is measured to that press.
    setSelectedAnswer(answer);
  };

  // Choice-question click handler — remembers WHICH POSITION was
  // tapped (in the shuffled array) so we can send it as
  // `selected_position`. Real value is preserved in selectedAnswer
  // for feedback rendering.
  const handleChoiceSelect = (position: number, answer: any) => {
    if (showingFeedback || answering) return;
    setSelectedPosition(position);
    setSelectedAnswer(answer);
    // single-choice: tap = submit (no separate "Răspunde" button).
    handleSubmitAnswer({ position, answer });
  };

  // Multi-choice — toggle a position in/out of the selected set. We
  // mirror the set into selectedAnswer (non-null when ≥1 checked) so the
  // existing submit-enabled gate keeps working.
  const handleMultiToggle = (position: number) => {
    if (showingFeedback || answering) return;
    const next = selectedPositionsMulti.includes(position)
      ? selectedPositionsMulti.filter((p) => p !== position)
      : [...selectedPositionsMulti, position];
    setSelectedPositionsMulti(next);
    setSelectedAnswer(next.length ? next : null);
  };

  // Compute the preview budget for the current question. Prefer
  // quiz-level override; else use the per-question auto_preview_ms
  // returned by the backend (word-count based); else fall back to
  // 4s so we still have a reading beat.
  const computePreviewMs = useCallback(() => {
    if (quizMeta?.preview_seconds != null) return quizMeta.preview_seconds * 1000;
    const q = shuffledQuestions[currentQuestionIndex];
    return q?.auto_preview_ms ?? 4000;
  }, [quizMeta, shuffledQuestions, currentQuestionIndex]);

  // Every time we enter the preview sub-phase (new question), reset
  // the preview countdown. When it hits 0, auto-advance to `answer`
  // sub-phase — same path as the "Am înțeles" tap.
  useEffect(() => {
    if (phase !== "questions") return;
    if (questionSubPhase !== "preview") return;
    if (shuffledQuestions.length === 0) return;
    const total = computePreviewMs();
    setPreviewMsLeft(total);
    const start = Date.now();
    const iv = setInterval(() => {
      const elapsed = Date.now() - start;
      const left = Math.max(0, total - elapsed);
      setPreviewMsLeft(left);
      if (left <= 0) {
        clearInterval(iv);
        handleReady();
      }
    }, 100);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, questionSubPhase, currentQuestionIndex]);

  // Tap "Am înțeles" (or auto-advance at preview end): tell the
  // server the athlete is ready, get the server-authoritative timer
  // window, then switch to the answer sub-phase with a live
  // countdown. If the quiz has no timer, we still transition — just
  // no ticking.
  const handleReady = useCallback(async () => {
    if (questionSubPhase !== "preview") return;
    const q = shuffledQuestions[currentQuestionIndex];
    if (!q || !attempt) return;
    try {
      const readyResp = await readyForQuestion(quizId!, attempt.id, q.id);
      setTimerMs(readyResp.timer_ms);
      setTimerMsLeft(readyResp.timer_ms);
      setQuestionSubPhase("answer");
    } catch (err) {
      console.error("Failed to signal ready:", err);
      // Fail-open — still let them answer without a timer if the
      // ready call breaks. Response validation on submit will still
      // stamp a response_ms based on attempt.started_at.
      setTimerMs(0);
      setTimerMsLeft(0);
      setQuestionSubPhase("answer");
    }
  }, [questionSubPhase, shuffledQuestions, currentQuestionIndex, attempt, quizId]);

  // Answer-phase countdown. Only ticks if the quiz has a timer.
  useEffect(() => {
    if (phase !== "questions") return;
    if (questionSubPhase !== "answer") return;
    if (timerMs <= 0) return; // no timer for this quiz
    if (showingFeedback) return; // stop ticking after submit
    const start = Date.now();
    const startingLeft = timerMsLeft;
    const iv = setInterval(() => {
      const elapsed = Date.now() - start;
      setTimerMsLeft(Math.max(0, startingLeft - elapsed));
    }, 100);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, questionSubPhase, timerMs, showingFeedback]);

  const handleSubmitAnswer = async (override?: {
    position?: number;
    positions?: number[];
    answer?: any;
  }) => {
    if (!quiz || !attempt || answering) return;

    const questions = getQuestionsForPhase();
    const currentQuestion = questions[currentQuestionIndex];

    // Resolve the selection — prefer the explicit override (auto-submit
    // on tap for single-choice) over React state, which may not
    // have flushed yet in the same tick.
    const posSingle = override?.position ?? selectedPosition ?? undefined;
    const posMulti = override?.positions ?? selectedPositionsMulti;
    const tfAnswer = override?.answer !== undefined ? override.answer : selectedAnswer;

    // Kind-appropriate "has a selection" guard.
    if (currentQuestion.kind === "true_false") {
      if (tfAnswer === null || tfAnswer === undefined) return;
    } else if (currentQuestion.kind === "multi") {
      if (!canSubmitMulti(posMulti.length, currentQuestion.correct_count)) return;
    } else if (posSingle === undefined) {
      return;
    }

    try {
      setAnswering(true);
      // Send selected_position for choice questions (server de-shuffles
      // to the real index), plain answer for true_false (never
      // shuffled). Contract mirrors what the answer endpoint expects.
      const payload =
        currentQuestion.kind === "true_false"
          ? { answer: tfAnswer }
          : currentQuestion.kind === "multi"
          ? { selectedPositions: posMulti }
          : { selectedPosition: posSingle };
      const response = await answerQuestion(
        quizId!,
        attempt.id,
        currentQuestion.id,
        payload
      );

      // Store the response for points info when quiz completes
      if (response.completed) {
        lastAnswerResponseRef.current = response;
        if (response.newly_earned_badges && response.newly_earned_badges.length > 0) {
          setEarnedBadges(response.newly_earned_badges);
          setShowBadgeCelebration(true);
        }
      }

      setFeedback(response);
      setShowingFeedback(true);
      setProgress(response.progress);
      if (response.lives_remaining !== undefined) setLives(response.lives_remaining);
      if ((response as any).lives) {
        setLivesInfo((response as any).lives);
        livesFetchedAtRef.current = Date.now();
      }

      // Sprint 1 juice: haptic + confetti on correct, error buzz on
      // wrong, and a live consecutive-correct streak.
      if (response.correct) {
        setAnswerStreak((s) => s + 1);
        hapticCorrect();
        fireConfetti();
      } else {
        setAnswerStreak(0);
        hapticWrong();
      }

      // Advance to the next question / result. Fires either when the
      // athlete taps "Continuă" or after the auto-advance delay,
      // whichever comes first. Guarded via the refs so it runs once.
      const advance = () => {
        if (feedbackTimeoutRef.current) {
          clearTimeout(feedbackTimeoutRef.current);
          feedbackTimeoutRef.current = null;
        }
        advanceRef.current = null;
        setShowingFeedback(false);
        setFeedback(null);
        setSelectedAnswer(null);
        setSelectedPosition(null);
        setSelectedPositionsMulti([]);
        setAnswering(false);
        // Every next question starts in the preview sub-phase so the
        // athlete gets reading time before the timer kicks in again.
        setQuestionSubPhase("preview");

        if (response.completed) {
          loadFinalResult();
        } else if (response.next_question_id) {
          const nextIndex = questions.findIndex((q) => q.id === response.next_question_id);
          if (nextIndex !== -1) {
            setCurrentQuestionIndex(nextIndex);
          } else if (quiz.video_mode === "chapters") {
            // Next question is in a different chapter — watch it first.
            setCurrentChapterIndex((prev) => prev + 1);
            setCurrentQuestionIndex(0);
            setPhase("video");
          } else {
            setCurrentQuestionIndex((prev) => prev + 1);
          }
        }
      };
      advanceRef.current = advance;
      // No auto-advance, in either direction.
      //
      // A correct answer used to move on by itself after 2.5 seconds. That is
      // plenty of time to see a green tick and nowhere near enough to read an
      // explanation, so the one screen that teaches something was the one
      // screen that got taken away — and on a slow question the athlete
      // learned to expect the jump, which makes the wrong-answer case (which
      // never had a timer) feel like the app froze.
      //
      // Leaving it to the tap costs one gesture and makes the feedback
      // readable at whatever pace the person reads at.
    } catch (error) {
      console.error("Failed to submit answer:", error);
      setAnswering(false);
    }
  };

  // Tap-to-continue: skip the auto-advance wait and move on immediately.
  const handleContinueAfterFeedback = () => {
    if (advanceRef.current) advanceRef.current();
  };

  // Fire the celebration (confetti + heavy haptic) once the result
  // screen mounts with freshly-earned badges.
  useEffect(() => {
    if (phase === "result" && showBadgeCelebration && earnedBadges.length > 0) {
      hapticCelebrate();
      fireConfetti({ big: true });
    }
  }, [phase, showBadgeCelebration, earnedBadges.length]);

  // Clear any pending auto-advance timer on unmount.
  useEffect(() => {
    return () => {
      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
    };
  }, []);

  const loadFinalResult = async () => {
    if (!attempt || !quizId) return;

    // Get points info from the last answer response (includes points_awarded, points_deducted, etc.)
    const lastResponse = lastAnswerResponseRef.current;

    try {
      const details = await getAttemptDetails(quizId, attempt.id);
      setFinalResult({
        score: details.score,
        max_score: details.max_score,
        correct_count: details.correct_count,
        wrong_count: details.wrong_count,
        points_earned: details.points_earned || 0,
        // Include detailed points breakdown from the last answer response
        points_awarded: lastResponse?.points_awarded,
        points_deducted: lastResponse?.points_deducted,
        net_points: lastResponse?.net_points,
        is_perfect_score: lastResponse?.is_perfect_score,
        perfect_bonus: lastResponse?.perfect_bonus,
        base_from_correct: lastResponse?.base_from_correct,
        speed_bonus: lastResponse?.speed_bonus,
        is_retake: lastResponse?.is_retake ?? retakeInfo?.is_retake,
        previous_best: lastResponse?.previous_best,
        out_of_lives: lastResponse?.out_of_lives,
        level_after: lastResponse?.level_after,
        leveled_up: lastResponse?.leveled_up,
      });
      setPhase("result");
    } catch (error) {
      console.error("Failed to load result:", error);
      // Fallback to progress data
      setFinalResult({
        score: progress.total_score,
        max_score: quiz?.max_points ?? (quiz?.questions?.reduce((s, q) => s + (q.points || 1), 0) || 0),
        correct_count: progress.correct_count,
        wrong_count: progress.wrong_count,
        points_earned: progress.total_score,
        points_awarded: lastResponse?.points_awarded,
        points_deducted: lastResponse?.points_deducted,
        net_points: lastResponse?.net_points,
        is_perfect_score: lastResponse?.is_perfect_score,
        perfect_bonus: lastResponse?.perfect_bonus,
        base_from_correct: lastResponse?.base_from_correct,
        speed_bonus: lastResponse?.speed_bonus,
        is_retake: lastResponse?.is_retake ?? retakeInfo?.is_retake,
        previous_best: lastResponse?.previous_best,
        out_of_lives: lastResponse?.out_of_lives,
        level_after: lastResponse?.level_after,
        leveled_up: lastResponse?.leveled_up,
      });
      setPhase("result");
    }
  };

  // "Reia testul" from the result screen — returns to the intro so the
  // retake warning + points penalty flow shows (penalty preserved).
  const handleRetryQuiz = () => {
    setFinalResult(null);
    setEarnedBadges([]);
    setShowBadgeCelebration(false);
    setAttempt(null);
    setShuffledQuestions([]);
    setCurrentQuestionIndex(0);
    setQuestionSubPhase("preview");
    setLives(3);
    setAnswerStreak(0);
    loadQuiz(); // refresh retakeInfo/previous attempts, then show intro
    setPhase("intro");
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">{t("common.loading", "Loading...")}</p>
        </div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <div className="text-5xl mb-4">404</div>
          <p className="text-slate-500">{t("quiz.not_found", "Quiz not found")}</p>
        </div>
      </div>
    );
  }

  // Back handler for mobile app — return to the origin screen (the category
  // the athlete opened the quiz from) when we know it, else the Quizzes list.
  const handleBack = () => {
    navigate(backTarget);
  };

  // INTRO PHASE - Quiz start screen
  if (phase === "intro") {
    // Lives gate for the intro screen. curLives comes from the last fetch;
    // the countdown is derived client-side and refetches on zero.
    const poolMax = livesInfo?.max ?? 5;
    const curLives = livesInfo?.lives ?? poolMax;
    const outOfLives = curLives <= 0;
    const msToNext =
      livesInfo && livesInfo.lives < livesInfo.max
        ? Math.max(0, livesInfo.ms_to_next_life - (nowTs - livesFetchedAtRef.current))
        : 0;
    const fmtCountdown = (ms: number) => {
      const s = Math.ceil(ms / 1000);
      return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    };
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header shows the back button + the athlete's lives (right) — not the
            quiz title, which the intro body already displays. */}
        <QuizHeader
          onBack={handleBack}
          right={
            <div className="flex items-center gap-1.5" title={t("quiz.lives", "vieți")}>
              <LivesBadge lives={curLives} max={livesInfo?.max ?? 5} />
              <span className="text-sm font-extrabold text-slate-700 tabular-nums">{curLives}</span>
              {curLives < poolMax && msToNext > 0 && (
                <span className="text-[11px] font-semibold text-slate-400 tabular-nums ml-0.5">{fmtCountdown(msToNext)}</span>
              )}
            </div>
          }
        />
        {/* Intro — same compact treatment: half the outer padding,
            title from text-3xl → text-lg, meta pills tighter, video
            info block compressed, and paddings pulled in so intro +
            start button fit above the fold. */}
        <div className="px-3 pt-3 pb-safe-bottom max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
            <h1 className="text-lg font-bold text-slate-800 mb-2 leading-snug">{quiz.title}</h1>
            {startError && (
              <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-800 text-sm font-semibold">
                <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v-3m0-4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" /></svg>
                <span>{startError}</span>
              </div>
            )}
            {quiz.description && (
              <div
                className="text-slate-500 text-sm mb-3 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: quiz.description }}
              />
            )}

            {quiz.content && quiz.content.replace(/<[^>]*>/g, "").trim() !== "" && (
              <div className="mb-3 rounded-xl border border-indigo-100 bg-indigo-50/50 overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 bg-indigo-100/60 text-indigo-800 text-xs font-bold uppercase tracking-wide">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                  Conținut
                </div>
                <div
                  className="px-3 py-3 text-slate-700 text-sm prose prose-sm max-w-none prose-headings:text-slate-800 prose-a:text-indigo-600"
                  dangerouslySetInnerHTML={{ __html: quiz.content }}
                />
              </div>
            )}

            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              <div className="flex items-center gap-1 bg-slate-50 rounded-full px-2.5 py-1">
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-slate-600 font-medium text-xs">{quiz.questions.length} {t("quiz.questions", "întrebări")}</span>
              </div>
              <div className="flex items-center gap-1 bg-slate-50 rounded-full px-2.5 py-1">
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="text-slate-600 font-medium text-xs">{t(`quiz.${quiz.difficulty}`, quiz.difficulty)}</span>
              </div>
              {(quiz.max_points ?? quiz.questions.reduce((s, q) => s + (q.points || 1), 0)) > 0 && (
                <div className="flex items-center gap-1 bg-amber-50 rounded-full px-2.5 py-1">
                  <svg className="w-3.5 h-3.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span
                    className="text-amber-600 font-medium text-xs"
                    title={t("quiz.points_bonus_hint", "Bonus de viteză și scor perfect")}
                  >
                    {t("quiz.points_plus_bonus", "{{points}} puncte + bonus", {
                      points: quiz.max_points ?? quiz.questions.reduce((s, q) => s + (q.points || 1), 0),
                    })}
                  </span>
                </div>
              )}
            </div>

            {/* Video Mode Info */}
            {quiz.video_mode && quiz.video_mode !== "none" && (
              <div className="bg-purple-50 rounded-lg p-2.5 mb-3 border border-purple-200">
                <div className="flex items-center gap-1.5 mb-1">
                  <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                  </svg>
                  <span className="font-medium text-purple-700 text-sm">
                    {quiz.video_mode === "full"
                      ? t("quiz.full_video_mode", "Full Video Mode")
                      : t("quiz.chapter_mode", "Chapter Mode")}
                  </span>
                </div>
                <p className="text-xs text-purple-600">
                  {quiz.video_mode === "full"
                    ? t("quiz.watch_full_video", "Watch the entire video before answering questions")
                    : t("quiz.watch_chapters", "Watch each chapter, then answer related questions")}
                </p>
                {quiz.video_mode === "chapters" && quiz.video_chapters && (
                  <div className="mt-2">
                    <p className="text-xs text-purple-500 mb-1">{quiz.video_chapters.length} chapters</p>
                    <div className="flex flex-wrap gap-1">
                      {quiz.video_chapters.map((chapter, idx) => (
                        <span key={idx} className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px]">
                          {chapter.title}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Previous Attempts — 2 rows max, compact */}
            {previousAttempts.length > 0 && (
              <div className="mb-3">
                <h3 className="text-xs font-medium text-slate-400 mb-1.5">
                  {t("quiz.previous_attempts", "Previous Attempts")}
                </h3>
                <div className="space-y-1">
                  {previousAttempts.slice(0, 2).map((a, idx) => (
                    <div key={a.id} className="flex items-center justify-between bg-slate-50 rounded p-2 text-xs">
                      <span className="text-slate-600">{t("quiz.attempt", "Attempt")} {idx + 1}</span>
                      <span className="text-slate-800 font-medium">
                        {a.score} {t("quiz.points", "puncte")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Retake Warning Banner */}
            {retakeInfo?.is_retake && retakeInfo.retake_deduction > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-2.5 mb-3">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="min-w-0">
                    <p className="font-semibold text-amber-800 text-sm">
                      {t("quiz.retake_warning_title", "Atenție - Reluare Quiz")}
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      {t("quiz.retake_warning_message", "Reluarea acestui quiz va costa")} <span className="font-bold">-{retakeInfo.retake_deduction} {t("quiz.points", "puncte")}</span>.
                    </p>
                    {retakeInfo.best_score > 0 && (
                      <p className="text-[10px] text-amber-600 mt-1">
                        {t("quiz.best_score", "Cel mai bun scor")}: {retakeInfo.best_score} {t("quiz.points", "puncte")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Out of lives — only shown when relevant. The heart count itself
                lives in the header now. */}
            {outOfLives && (
              <p className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs font-semibold text-rose-600">
                {t("quiz.out_of_lives_wait", "Nu mai ai vieți. Așteaptă reîncărcarea unei vieți ca să poți continua.")}
              </p>
            )}

            <button
              onClick={handleStartQuiz}
              disabled={outOfLives}
              className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
            >
              {outOfLives
                ? `${t("quiz.next_life_in", "următoarea în")} ${fmtCountdown(msToNext)}`
                : previousAttempts.length > 0
                ? t("quiz.retake_quiz", "Reia Quiz-ul")
                : t("quiz.start_quiz", "Începe Quiz-ul")}
            </button>

            {curLives < (livesInfo?.hard_cap ?? 60) && canBuyLives && (
              <button
                onClick={() => navigate("/app/lives")}
                className="mt-2 w-full px-3 py-2 rounded-lg text-emerald-700 text-sm font-semibold hover:bg-emerald-50"
              >
                {t("quiz.buy_lives", "Cumpără vieți")}
              </button>
            )}
          </div>
        </div>

        {/* Retake Confirmation Modal */}
        {showRetakeConfirm && retakeInfo && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">
                  {t("quiz.confirm_retake_title", "Confirmare Reluare")}
                </h3>
                <p className="text-slate-600">
                  {t("quiz.confirm_retake_message", "Sigur vrei să reiei acest quiz?")}
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-red-700">{t("quiz.points_deduction", "Cost reluare")}:</span>
                  <span className="text-red-700 font-bold text-lg">-{retakeInfo.retake_deduction} {t("quiz.points", "puncte")}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowRetakeConfirm(false)}
                  className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <button
                  onClick={handleConfirmRetake}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
                >
                  {t("quiz.confirm_retake", "Reia Quiz-ul")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // VIDEO PHASE - Watch video before questions
  if (phase === "video") {
    const videoUrl = quiz.video_url;
    const isChapterMode = quiz.video_mode === "chapters";
    const currentChapter = isChapterMode && quiz.video_chapters
      ? quiz.video_chapters[currentChapterIndex]
      : null;
    const youtubeVideoId = videoUrl ? getYouTubeVideoId(videoUrl) : null;
    const isYouTube = !!youtubeVideoId;

    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-indigo-950 text-white">
        {/* Ambient colour glows behind everything for a rich, cinematic feel. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-20 w-72 h-72 rounded-full bg-indigo-600/25 blur-3xl" />
          <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-fuchsia-600/20 blur-3xl" />
          <div className="absolute -bottom-16 left-1/4 w-72 h-72 rounded-full bg-cyan-500/15 blur-3xl" />
        </div>

        <header className="relative z-20 safe-area-top">
          <div className="flex items-center gap-3 px-4 py-3">
            <button
              onClick={handleBack}
              className="p-2 -ml-1 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/10 transition-colors"
              aria-label={t("common.back", "Înapoi")}
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.14em] text-indigo-300/80 font-semibold">
                {t("quiz.video_lesson", "Lecție video")}
              </p>
              <h1 className="text-base font-bold text-white truncate leading-tight">
                {quiz.title}
              </h1>
            </div>
          </div>
        </header>

        <div className="relative z-10 px-4 pt-2 pb-safe-bottom max-w-2xl mx-auto">
          {/* Chapter Info */}
          {isChapterMode && currentChapter && (
            <div className="mb-4 flex justify-center">
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-purple-500/20 text-purple-200 border border-purple-400/30 rounded-full text-xs font-semibold backdrop-blur-sm">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.247m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.247" />
                </svg>
                {t("quiz.chapter", "Capitol")} {currentChapterIndex + 1}: {currentChapter.title}
              </span>
            </div>
          )}

          {/* Video Player.
              A soft gradient halo sits behind the card (blurred, -inset) to
              give the frame a glowing edge. `relative isolate` on the inner
              wrapper creates a stacking context so the YouTube iframe (inside
              an `absolute inset-0` shell) cannot float above siblings like the
              "Continue to questions" button. `overflow-hidden` clips the iframe
              to the card's rounded corners. */}
          <div className="relative mb-5">
            <div aria-hidden className="absolute -inset-0.5 rounded-3xl bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-cyan-400 opacity-40 blur-lg" />
            <div className="relative isolate bg-black rounded-3xl overflow-hidden ring-1 ring-white/15 shadow-2xl">
            {isYouTube ? (
              /* YouTube Embed with Custom Controls */
              <div ref={ytWrapperRef} className="relative w-full aspect-video bg-black overflow-hidden">
                {/* The iframe fills the frame at its natural 16:9 size — no
                    artificial scaling, so the video is shown at its original
                    dimensions. YouTube's own chrome (title bar, logo, controls)
                    is kept out of the way by the full-cover overlays below,
                    which sit above the iframe and swallow every click. The rule
                    targets the iframe by id because YT.Player() replaces our div
                    with an <iframe> that inherits the id but not the classes. */}
                <style>{`#yt-player-container{position:absolute;inset:0;width:100%;height:100%;}`}</style>
                {/* YouTube Player Container - needs explicit sizing for iframe */}
                <div
                  id="yt-player-container"
                  className="absolute inset-0 w-full h-full"
                  style={{ minHeight: '200px' }}
                />

                {/* Subtle top scrim so YouTube's title bar (which fades in for a
                    moment when playback starts) reads as part of the cinematic
                    frame rather than clutter. Thin + light, so the video itself
                    stays uncropped and at full size. */}
                <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-black/50 to-transparent z-[5]" />

                {/* Fullscreen button — top-right, above the play/pause
                    overlay. stopPropagation so tapping it doesn't also
                    toggle play/pause on the parent overlay div. Targets
                    the wrapper div (this element) rather than the iframe,
                    so the auto-fullscreen guard (which only bounces
                    IFRAME fullscreens) lets it through. */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isVideoFullscreen) {
                      exitFullscreen();
                    } else {
                      requestFullscreen(ytWrapperRef.current);
                    }
                  }}
                  // Offset with env(safe-area-inset-top) + right so the
                  // button clears the notch / Dynamic Island once the
                  // wrapper takes over the viewport in fullscreen. The
                  // fullscreen element becomes the CSS root for env(),
                  // so in landscape the top inset is 0 and the button
                  // sits flush against the top-right corner.
                  style={{
                    top: "calc(env(safe-area-inset-top, 0px) + 0.5rem)",
                    right: "calc(env(safe-area-inset-right, 0px) + 0.5rem)",
                  }}
                  className="absolute z-20 bg-black/60 hover:bg-black/80 rounded-lg p-2 text-white transition-all"
                  aria-label={
                    isVideoFullscreen
                      ? t("quiz.exit_fullscreen", "Exit fullscreen")
                      : t("quiz.fullscreen", "Fullscreen")
                  }
                >
                  {isVideoFullscreen ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4m0 0H4m5 0L4 9m11-5v5m0 0h5m-5 0l5-5M9 15H4m5 0v5m0-5l-5 5m11 0v-5m0 0h5m-5 0l5 5" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  )}
                </button>

                {/* Custom Overlay for Play/Pause. While the video is NOT
                    playing (cued or paused) we make this overlay fully opaque
                    (bg-black) so YouTube's own chrome — the clip title, the
                    channel/share/"watch on YouTube" link icons and the
                    end-screen related-video grid, all of which YT paints on the
                    poster/paused state — is completely hidden behind it. During
                    playback the overlay is transparent so the video shows. It
                    also sits above the iframe (z-10), so every YT click target
                    is swallowed here: no navigation off to YouTube. */}
                <div
                  className={`absolute inset-0 flex items-center justify-center cursor-pointer z-10 group transition-colors ${
                    ytPlaying ? "" : "bg-black"
                  }`}
                  onClick={() => {
                    if (ytPlayer && ytReady) {
                      if (ytPlaying) {
                        ytPlayer.pauseVideo();
                      } else {
                        ytPlayer.playVideo();
                      }
                    }
                  }}
                >
                  {/* Play Button - shown when paused */}
                  {!ytPlaying && ytReady && (
                    <div className="bg-black/50 hover:bg-black/70 rounded-full p-6 transition-all transform hover:scale-110">
                      <svg className="w-16 h-16 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  )}

                  {/* Pause Button - shown on hover when playing */}
                  {ytPlaying && ytReady && (
                    <div className="bg-black/30 hover:bg-black/50 rounded-full p-6 transition-all opacity-0 group-hover:opacity-100 transform hover:scale-110">
                      <svg className="w-16 h-16 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                      </svg>
                    </div>
                  )}

                  {/* Loading indicator while API loads */}
                  {!ytReady && !ytError && (
                    <div className="bg-black/50 rounded-full p-6">
                      <div className="animate-spin w-16 h-16 border-4 border-white border-t-transparent rounded-full"></div>
                    </div>
                  )}

                  {/* Error state - show simple play message */}
                  {ytError && (
                    <div className="bg-red-500/80 rounded-xl p-4 text-white text-center">
                      <p className="text-sm">API YouTube indisponibil</p>
                      <p className="text-xs opacity-75">Folosește butonul de mai jos</p>
                    </div>
                  )}
                </div>

                {/* No dark masks any more — the iframe scale-crop above pushes
                    YouTube's title/logo/controls outside the frame, so the
                    playing video is shown clean, with nothing painted over it. */}
              </div>
            ) : (
              /* Regular Video Player — native <video> has controls
                  which already includes a fullscreen button on Android
                  + desktop. We overlay an extra explicit button anyway
                  so the entry-point stays consistent with the YouTube
                  wrapper (users learn one gesture) and so iOS Safari
                  in the Capacitor WebView, which sometimes hides the
                  native FS button, still has a reliable way in. */
              <div className="relative w-full aspect-video">
                <video
                  ref={videoRef}
                  src={videoUrl || ""}
                  controls
                  playsInline
                  className="w-full h-full"
                  onTimeUpdate={(e) => {
                    const video = e.currentTarget;
                    handleVideoProgress(video.currentTime, video.duration);
                  }}
                  onPause={(e) => saveVideoProgress(e.currentTarget.currentTime, e.currentTarget.duration, true)}
                  onEnded={(e) => {
                    // Save the full position before handleVideoComplete moves on.
                    saveVideoProgress(e.currentTarget.duration, e.currentTarget.duration, true);
                    handleVideoComplete();
                  }}
                >
                  Your browser does not support the video tag.
                </video>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    requestFullscreen(videoRef.current);
                  }}
                  className="absolute top-2 right-2 z-20 bg-black/50 hover:bg-black/70 rounded-lg p-2 text-white transition-all"
                  aria-label={t("quiz.fullscreen", "Fullscreen")}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </button>
              </div>
            )}
            </div>
          </div>

          {/* Progress panel — glassy dark card that sits on the gradient. */}
          <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm p-4 mb-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <span className="text-sm font-medium text-slate-300">
                {isChapterMode
                  ? `${t("quiz.chapter", "Capitol")} ${currentChapterIndex + 1} / ${quiz.video_chapters?.length || 0}`
                  : t("quiz.watching_video", "Vizionează videoclipul")}
              </span>
              {videoCompleted ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 text-xs font-semibold">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {t("quiz.video_completed", "Completat")}
                </span>
              ) : (
                <span className="text-xs font-semibold text-slate-400 tabular-nums whitespace-nowrap">
                  {isYouTube && ytDuration > 0 ? (
                    // Show actual time progress for YouTube
                    <>
                      {Math.floor(ytCurrentTime / 60)}:{String(Math.floor(ytCurrentTime % 60)).padStart(2, '0')} / {Math.floor(ytDuration / 60)}:{String(Math.floor(ytDuration % 60)).padStart(2, '0')} · {videoWatchPercent}%
                    </>
                  ) : isYouTube ? (
                    ytError
                      ? t("quiz.api_error", "Folosește butonul de confirmare")
                      : ytReady
                        ? t("quiz.click_to_play", "Click pentru a porni")
                        : t("quiz.loading_video", "Se încarcă...")
                  ) : (
                    `${videoWatchPercent}%`
                  )}
                </span>
              )}
            </div>

            {/* Progress Bar — gradient fill with a soft glow. */}
            <div className="h-2.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  videoWatchPercent >= 90
                    ? "bg-gradient-to-r from-emerald-400 to-teal-400 shadow-[0_0_12px_rgba(52,211,153,0.5)]"
                    : "bg-gradient-to-r from-indigo-400 to-fuchsia-400 shadow-[0_0_12px_rgba(129,140,248,0.45)]"
                }`}
                style={{ width: `${videoWatchPercent}%` }}
              />
            </div>

            {/* YouTube Playing Indicator */}
            {isYouTube && ytPlaying && !videoCompleted && (
              <div className="flex items-center justify-center gap-2 mt-3 text-indigo-300 text-xs font-medium">
                <div className="flex items-end gap-0.5 h-4">
                  <div className="w-1 bg-indigo-400 rounded-full animate-pulse" style={{ height: '60%', animationDelay: '0ms' }} />
                  <div className="w-1 bg-fuchsia-400 rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
                  <div className="w-1 bg-indigo-400 rounded-full animate-pulse" style={{ height: '75%', animationDelay: '300ms' }} />
                  <div className="w-1 bg-cyan-400 rounded-full animate-pulse" style={{ height: '45%', animationDelay: '450ms' }} />
                </div>
                <span>{t("quiz.playing", "Se redă...")}</span>
              </div>
            )}

            {/* Fallback: manual confirm ONLY when the YouTube API genuinely
                fails (otherwise a first-timer could bypass the video before
                it even plays). Retakers get a proper skip button below. */}
            {isYouTube && !videoCompleted && ytError && (
              <button
                onClick={() => {
                  setVideoWatchPercent(100);
                  setVideoCompleted(true);
                }}
                className="mt-4 w-full px-4 py-3 bg-amber-500/90 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t("quiz.confirm_watched", "Am vizionat videoclipul")}
              </button>
            )}
          </div>

          {/* Continue Button */}
          {videoCompleted && (
            <button
              onClick={() => setPhase("questions")}
              className="group w-full px-6 py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-semibold shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all flex items-center justify-center gap-2"
            >
              {t("quiz.continue_to_questions", "Continuă la întrebări")}
              <svg className="w-5 h-5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          )}

          {/* Retake: the athlete has already completed this quiz once, so
              let them skip straight to the questions. First attempt: they
              must watch ≥90% (no skip). */}
          {!videoCompleted && retakeInfo?.is_retake && (
            <button
              onClick={() => setVideoCompleted(true)}
              className="w-full px-6 py-3.5 rounded-2xl bg-white/10 hover:bg-white/15 text-slate-200 border border-white/10 font-medium transition-all"
            >
              {t("quiz.skip_video_seen", "Sari peste video (l-ai vizionat deja)")}
            </button>
          )}
          {!videoCompleted && !retakeInfo?.is_retake && (
            <p className="text-center text-slate-400/90 text-xs mt-3">
              {t("quiz.watch_90_percent", "Vizionează cel puțin 90% din video pentru a continua")}
            </p>
          )}
        </div>
      </div>
    );
  }

  // QUESTIONS PHASE - Answer questions one by one
  if (phase === "questions") {
    const questions = getQuestionsForPhase();
    const currentQuestion = questions[currentQuestionIndex];
    const isChapterMode = quiz.video_mode === "chapters";
    const currentChapter = isChapterMode && quiz.video_chapters
      ? quiz.video_chapters[currentChapterIndex]
      : null;

    if (!currentQuestion) {
      return (
        <div className="flex items-center justify-center h-screen bg-slate-50">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">{t("quiz.loading_question", "Loading question...")}</p>
          </div>
        </div>
      );
    }

    // Compact layout so quiz + question + answers + submit fit on one
    // mobile screen without scrolling: outer padding halved, badges
    // (type + points) folded into the progress bar row so they no
    // longer occupy their own strip, question card padding cut from
    // p-8 to p-3, question text stepped down to text-base, answer
    // buttons tightened to p-2.5 with smaller radios.
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header carries the live HUD: lives, streak, correct/wrong and the
            running score — so the question area stays clean. */}
        <QuizHeader
          onBack={handleBack}
          right={
            <div className="flex items-center gap-2 text-xs">
              <LivesBadge lives={lives} max={livesInfo?.max ?? 5} />
              {answerStreak >= 2 && (
                <span
                  key={answerStreak}
                  className="flex items-center gap-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5 font-bold animate-in zoom-in-50 duration-300"
                >
                  🔥 {answerStreak}
                </span>
              )}
              <span className="flex items-center gap-0.5 text-emerald-600 font-bold tabular-nums">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                {progress.correct_count}
              </span>
              <span className="flex items-center gap-0.5 text-red-500 font-bold tabular-nums">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                {progress.wrong_count}
              </span>
              <span className="flex items-center gap-0.5 text-amber-600 font-bold tabular-nums">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                {progress.total_score}
              </span>
            </div>
          }
        />
        <div className="px-3 pt-3 pb-safe-bottom max-w-3xl mx-auto">
          {/* Chromeless meta: question index + type/points pills on one row,
              a slim progress rail below. No card, so it takes little height. */}
          <div className="mb-2.5">
            <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
              {isChapterMode && currentChapter && (
                <span className="text-purple-600 font-medium text-[11px] truncate">{currentChapter.title}:</span>
              )}
              <span className="text-slate-500 text-xs font-semibold whitespace-nowrap">
                {t("quiz.question", "Question")} {currentQuestionIndex + 1}/{questions.length}
              </span>
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                currentQuestion.kind === "single"
                  ? "bg-blue-100 text-blue-700"
                  : currentQuestion.kind === "multi"
                  ? "bg-purple-100 text-purple-700"
                  : "bg-green-100 text-green-700"
              }`}>
                {currentQuestion.kind === "single"
                  ? t("quiz.single_answer", "Răspuns unic")
                  : currentQuestion.kind === "multi"
                  ? t("quiz.multiple_answer", "Răspunsuri multiple")
                  : t("quiz.true_false", "Adevărat / Fals")}
              </span>
              {currentQuestion.kind === "multi" && (currentQuestion.correct_count ?? 0) > 0 && (
                <span className="px-1.5 py-0.5 bg-purple-600 text-white rounded-full text-[10px] font-bold whitespace-nowrap">
                  {t("quiz.pick_answers", { count: currentQuestion.correct_count as number })}
                </span>
              )}
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-medium whitespace-nowrap">
                {currentQuestion.points || 1} {t("quiz.points", "pts")}
              </span>
            </div>
            <div className="h-1 bg-slate-200/70 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${(progress.answered / progress.total) * 100}%` }}
              />
            </div>
          </div>

          {/* Timer moved into the question card as a circular ring
              (CircularTimer), so the countdown sits right next to the
              question the athlete is racing to answer. */}

          {/* Question card — compact padding, tighter question text. */}
          <div className={`bg-white rounded-xl shadow-md border-2 p-3 transition-all duration-300 ${
            showingFeedback
              ? feedback?.correct
                ? "border-emerald-400 bg-emerald-50"
                : "border-red-400 bg-red-50"
              : "border-slate-200"
          }`}>
            {/* Question image — rendered when the question has an image
                attached (media_url). Was stored but never shown before. */}
            {currentQuestion.media_type === "image" && resolveMediaUrl(currentQuestion.media_url) && (
              <img
                src={resolveMediaUrl(currentQuestion.media_url)!}
                alt=""
                className="w-full max-h-48 object-contain rounded-lg border border-slate-200 bg-slate-50 mb-3"
              />
            )}
            <div className="flex items-start gap-3 mb-3">
              <h2 className="flex-1 text-base font-bold text-slate-800 leading-snug">{currentQuestion.text}</h2>
              {questionSubPhase === "answer" && timerMs > 0 && !showingFeedback && (
                <CircularTimer msLeft={timerMsLeft} msTotal={timerMs} />
              )}
            </div>

            {/* Preview sub-phase: options are HIDDEN. Show a large
                "Am înțeles" button that transitions to the answer
                phase. Auto-advances when previewMsLeft hits 0. */}
            {questionSubPhase === "preview" && !showingFeedback ? (
              <div className="py-4">
                <p className="text-xs text-slate-500 text-center mb-3">
                  {t("quiz.readAndTap", "Citește întrebarea, apoi apasă pentru a începe")}
                </p>
                <button
                  onClick={handleReady}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-semibold text-sm transition-all shadow-md active:scale-[0.98]"
                >
                  {t("quiz.iUnderstand", "Răspunde")}
                </button>
                <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-slate-300 rounded-full transition-all duration-100"
                    style={{ width: `${100 - (previewMsLeft / Math.max(1, computePreviewMs())) * 100}%` }}
                  />
                </div>
              </div>
            ) : (
            <>

            {/* Answer Options — tight padding + smaller radio */}
            <div className="space-y-1.5">
              {currentQuestion.kind === "single" &&
                Array.isArray(currentQuestion.options) &&
                currentQuestion.options.map((option: string, idx: number) => {
                  // idx = position in the SHUFFLED array the client sees.
                  // The server maps the real correct index back to the
                  // position(s) in this shuffled array via
                  // `correct_position`, so we can highlight the right
                  // option in green even when the athlete picked a wrong
                  // one — matching the "Răspunsul corect e marcat mai sus"
                  // feedback line.
                  const isSelected = selectedPosition === idx;
                  const cp = feedback?.correct_position;
                  const isCorrectAnswer =
                    showingFeedback &&
                    (Array.isArray(cp) ? cp.includes(idx) : cp === idx);
                  const isWrongSelection = showingFeedback && isSelected && !feedback?.correct;

                  return (
                    <button
                      key={idx}
                      disabled={showingFeedback || answering}
                      onClick={() => handleChoiceSelect(idx, option)}
                      className={`w-full text-left p-2.5 rounded-lg border-2 transition-all duration-200 ${
                        isCorrectAnswer
                          ? "border-emerald-500 bg-emerald-100"
                          : isWrongSelection
                          ? "border-red-500 bg-red-100"
                          : isSelected
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-200 bg-slate-50 hover:border-slate-300"
                      } ${showingFeedback || answering ? "cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center ${
                            isCorrectAnswer
                              ? "border-emerald-500 bg-emerald-500"
                              : isWrongSelection
                              ? "border-red-500 bg-red-500"
                              : isSelected
                              ? "border-blue-500 bg-blue-500"
                              : "border-slate-300"
                          }`}
                        >
                          {(isSelected || isCorrectAnswer) && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <span className={`text-sm ${
                          isCorrectAnswer ? "text-emerald-800 font-medium" :
                          isWrongSelection ? "text-red-800" :
                          "text-slate-800"
                        }`}>
                          {option}
                        </span>
                      </div>
                    </button>
                  );
                })}

              {/* Multi-choice — checkboxes; the athlete picks every
                  correct option. Scoring is all-or-nothing (server-side).
                  After feedback each option is marked: picked-correct
                  (solid green), missed-correct (dashed green), or
                  picked-wrong (red). Correct positions come from the
                  server's correct_position array. */}
              {currentQuestion.kind === "multi" &&
                Array.isArray(currentQuestion.options) &&
                currentQuestion.options.map((option: string, idx: number) => {
                  const isSelected = selectedPositionsMulti.includes(idx);
                  const cp = feedback?.correct_position;
                  const isCorrectAnswer =
                    showingFeedback && Array.isArray(cp) && cp.includes(idx);
                  const isWrongSelection = showingFeedback && isSelected && !isCorrectAnswer;
                  const isMissed = showingFeedback && !isSelected && isCorrectAnswer;

                  return (
                    <button
                      key={idx}
                      disabled={showingFeedback || answering}
                      onClick={() => handleMultiToggle(idx)}
                      className={`w-full text-left p-2.5 rounded-lg border-2 transition-all duration-200 ${
                        isCorrectAnswer
                          ? isMissed
                            ? "border-emerald-500 border-dashed bg-emerald-50"
                            : "border-emerald-500 bg-emerald-100"
                          : isWrongSelection
                          ? "border-red-500 bg-red-100"
                          : isSelected
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-200 bg-slate-50 hover:border-slate-300"
                      } ${showingFeedback || answering ? "cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center ${
                            isCorrectAnswer
                              ? "border-emerald-500 bg-emerald-500"
                              : isWrongSelection
                              ? "border-red-500 bg-red-500"
                              : isSelected
                              ? "border-blue-500 bg-blue-500"
                              : "border-slate-300"
                          }`}
                        >
                          {(isSelected || isCorrectAnswer) && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 20 20">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 10l4 4 8-9" />
                            </svg>
                          )}
                        </div>
                        <span className={`text-sm ${
                          isCorrectAnswer ? "text-emerald-800 font-medium" :
                          isWrongSelection ? "text-red-800" :
                          "text-slate-800"
                        }`}>
                          {option}
                        </span>
                        {isMissed && (
                          <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-emerald-600">
                            {t("quiz.missed", "ratat")}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}

              {currentQuestion.kind === "true_false" && (
                <>
                  {[true, false].map((value) => {
                    const isSelected = selectedAnswer === value;
                    const isCorrectAnswer = showingFeedback && feedback?.correct_answer === value;
                    const isWrongSelection = showingFeedback && isSelected && !feedback?.correct;

                    return (
                      <button
                        key={String(value)}
                        disabled={showingFeedback || answering}
                        onClick={() => handleAnswerSelect(value)}
                        className={`w-full text-left p-2.5 rounded-lg border-2 transition-all duration-200 ${
                          isCorrectAnswer
                            ? "border-emerald-500 bg-emerald-100"
                            : isWrongSelection
                            ? "border-red-500 bg-red-100"
                            : isSelected
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-200 bg-slate-50 hover:border-slate-300"
                        } ${showingFeedback || answering ? "cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center ${
                              isCorrectAnswer
                                ? "border-emerald-500 bg-emerald-500"
                                : isWrongSelection
                                ? "border-red-500 bg-red-500"
                                : isSelected
                                ? "border-blue-500 bg-blue-500"
                                : "border-slate-300"
                            }`}
                          >
                            {(isSelected || isCorrectAnswer) && (
                              <div className="w-2 h-2 bg-white rounded-full" />
                            )}
                          </div>
                          <span className={`text-sm ${
                            isCorrectAnswer ? "text-emerald-800 font-medium" :
                            isWrongSelection ? "text-red-800" :
                            "text-slate-800"
                          }`}>
                            {value ? t("quiz.true", "True") : t("quiz.false", "False")}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            {/* Feedback Message — compact one-line variant with a
                speed chip on the right side of correct answers.
                "Instant!" (<1s), "Rapid!" (<2s) or a subtle time
                readout for slower correct answers. */}
            {showingFeedback && feedback && (
              <div className={`mt-2.5 p-2.5 rounded-lg flex items-center gap-2 ${
                feedback.correct
                  ? "bg-emerald-100 border border-emerald-300"
                  : "bg-red-100 border border-red-300"
              }`}>
                {feedback.correct ? (
                  <>
                    <div className="w-7 h-7 shrink-0 bg-emerald-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-emerald-800 text-sm">
                        {t("quiz.correct", "Correct!")}
                        {feedback.response_ms !== undefined && feedback.response_ms < 1000 && (
                          <span className="ml-2 px-1.5 py-0.5 bg-amber-400 text-amber-900 rounded-full text-[10px] font-bold uppercase tracking-wider">
                            ⚡ Instant!
                          </span>
                        )}
                        {feedback.response_ms !== undefined && feedback.response_ms >= 1000 && feedback.response_ms < 2000 && (
                          <span className="ml-2 px-1.5 py-0.5 bg-orange-400 text-orange-900 rounded-full text-[10px] font-bold uppercase tracking-wider">
                            🚀 Rapid!
                          </span>
                        )}
                      </p>
                      <p className="text-emerald-600 text-xs">
                        +{feedback.points_earned} {t("quiz.points", "points")}
                        {feedback.response_ms !== undefined && (
                          <span className="ml-1 text-emerald-500/70">
                            · {(feedback.response_ms / 1000).toFixed(1)}s
                          </span>
                        )}
                      </p>
                      {/* Social comparison — rotates between percentile,
                          delta-vs-avg, and top-N flavour so it doesn't
                          get stale over 5 questions. Only shown when the
                          server actually has peer data. */}
                      {/* One social/speed line — computed first, rendered
                          only if there's actually a message (no empty
                          paragraph), so a fast mid-pack answer still gets
                          the speed praise. */}
                      {(() => {
                        const s = feedback.social;
                        let msg: string | null = null;
                        if (s) {
                          const options: string[] = [];
                          if (s.percentile_team >= 60) {
                            options.push(`🎯 Ai răspuns mai rapid decât ${s.percentile_team}% dintre colegii din echipă`);
                          }
                          if (s.delta_vs_team_avg_ms > 500) {
                            options.push(`⚡ Cu ${(s.delta_vs_team_avg_ms / 1000).toFixed(1)}s mai rapid decât media echipei`);
                          }
                          if (s.faster_teammates === 0 && s.team_peers >= 3) {
                            options.push(`🏆 Cel mai rapid din echipă la această întrebare!`);
                          } else if (s.faster_teammates < 3 && s.team_peers >= 3) {
                            options.push(`🥇 Top ${s.faster_teammates + 1} din echipă la această întrebare`);
                          }
                          if (options.length > 0) msg = options[currentQuestionIndex % options.length];
                        }
                        // Speed praise — only on TIMED quizzes (untimed
                        // forces speed_factor to 1, not a real speed
                        // signal) and only if no social message qualified.
                        if (!msg && feedback.correct && timerMs > 0 && (feedback.speed_factor ?? 0) >= 0.75) {
                          msg = "⚡ " + t("quiz.fast_answer", "Răspuns rapid! Bonus de viteză");
                        }
                        if (!msg) return null;
                        return <p className="text-emerald-700/80 text-[11px] mt-1 font-medium">{msg}</p>;
                      })()}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-7 h-7 shrink-0 bg-red-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-red-800 text-sm">{t("quiz.wrong", "Wrong!")}</p>
                      <p className="text-red-600 text-xs">{t("quiz.the_correct_answer_was", "The correct answer was shown above")}</p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Explanation — the "why is this correct" note, shown after
                answering when the admin provided one. */}
            {showingFeedback && feedback?.explanation && (
              <div className="mt-2.5 rounded-lg border border-blue-200 border-l-[3px] border-l-blue-500 bg-blue-50 p-3 animate-in fade-in slide-in-from-bottom-2">
                <p className="text-[11px] font-extrabold uppercase tracking-wider text-blue-600 mb-1 flex items-center gap-1.5">
                  💡 {t("quiz.explanation", "Explicație")}
                </p>
                <p className="text-[13px] leading-relaxed text-blue-900">{feedback.explanation}</p>
              </div>
            )}

            {/* The only way forward, now that nothing advances on its own.
                Same prominence whether the answer was right or wrong: it is
                the single control on the screen and the athlete should not
                have to hunt for it. */}
            {showingFeedback && (
              <button
                onClick={handleContinueAfterFeedback}
                className="w-full mt-2.5 px-4 py-3.5 text-white rounded-lg font-bold text-sm transition-all duration-200 active:scale-[0.98] bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/25"
              >
                {feedback?.completed
                  ? t("quiz.seeResult", "Vezi rezultatul")
                  : t("quiz.nextQuestion", "Următoarea întrebare")}{" "}
                →
              </button>
            )}

            {/* Submit Button — multi-choice and true/false. Single-choice
                auto-submits on tap, so no button is needed there. Multi stays
                disabled until exactly the announced number of options is
                picked; true/false until one of the two is picked. */}
            {!showingFeedback && (currentQuestion.kind === "multi" || currentQuestion.kind === "true_false") && (
              <button
                onClick={() => handleSubmitAnswer()}
                disabled={
                  answering ||
                  (currentQuestion.kind === "multi"
                    ? !canSubmitMulti(selectedPositionsMulti.length, currentQuestion.correct_count)
                    : !canConfirmTrueFalse(selectedAnswer))
                }
                className="w-full mt-3 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {answering
                  ? t("quiz.submitting", "Se trimite...")
                  : currentQuestion.kind === "true_false"
                  ? t("quiz.confirm_answer", "Confirmă")
                  : t("quiz.submit_answer", "Confirmă răspunsurile")}
              </button>
            )}
            </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // RESULT PHASE - Show final results
  if (phase === "result" && finalResult) {
    // Accuracy = share of QUESTIONS answered correctly (3/5 = 60%), which
    // is what the athlete expects — not a points ratio.
    // Denominator = the whole quiz, not just answered questions — so an
    // out-of-lives finish (e.g. 5 right of 20, quit on lives) reads as
    // 25%, not an inflated 63% of the answered subset.
    const answeredCount = finalResult.correct_count + finalResult.wrong_count;
    const totalQuestions = quiz?.questions?.length || answeredCount;
    const percentage = totalQuestions > 0 ? Math.round((finalResult.correct_count / totalQuestions) * 100) : 0;
    const passed = percentage >= 60;
    const stars = percentage >= 90 ? 3 : percentage >= 60 ? 2 : percentage >= 30 ? 1 : 0;
    const ringColor = passed ? "#10b981" : percentage >= 30 ? "#f59e0b" : "#ef4444";
    // Gross points earned this run = full base points from correct answers
    // PLUS the speed bonus earned on top. We show the two as separate lines.
    const grossEarned = finalResult.score || 0;
    const speedBonus = finalResult.speed_bonus ?? 0;
    // Base (full question points) from correct answers. Fall back to
    // gross − speed bonus for older responses that didn't send the split.
    const baseFromCorrect = finalResult.base_from_correct ?? (grossEarned - speedBonus);
    const awarded = finalResult.points_awarded ?? 0;
    const deducted = finalResult.points_deducted ?? 0;
    // First-time perfect-score bonus (0 when none / already perfect before).
    const perfectBonus = finalResult.perfect_bonus ?? 0;
    // On a retake, points only credit when you beat your previous best,
    // so gross-earned can exceed what actually hit the ledger.
    const isRetake = !!finalResult.is_retake;
    const prevBest = finalResult.previous_best ?? 0;
    const beatBy = grossEarned - prevBest; // >0 means new personal best
    const retakeNoImprove = isRetake && grossEarned > 0 && awarded < grossEarned;

    return (
      <div className="min-h-screen bg-slate-50">
        <QuizHeader title={t("quiz.results", "Results")} onBack={handleBack} />

        {/* Full-screen badge-unlock celebration. Overlays the result
            screen when the completion earned new badges; confetti +
            haptic fire from the effect above. Tap to dismiss. */}
        {showBadgeCelebration && earnedBadges.length > 0 && (
          <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-200">
            <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center animate-in zoom-in-75 duration-300">
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-fuchsia-500 mb-1">
                🎉 {earnedBadges.length > 1 ? t("quiz.new_badges", "Insigne noi") : t("quiz.new_badge", "Insignă nouă")}
              </p>
              <div className="space-y-3 my-4">
                {earnedBadges.map((b) => (
                  <div key={b.id} className="flex flex-col items-center">
                    <div
                      className="w-24 h-24 rounded-3xl grid place-items-center text-5xl overflow-hidden animate-in zoom-in-50 duration-500"
                      style={{ backgroundColor: `${b.color}22`, color: b.color }}
                    >
                      {b.icon_image_url ? (
                        <img src={b.icon_image_url} alt={b.name} className="w-full h-full object-contain p-2" />
                      ) : (
                        b.icon_emoji || "🏅"
                      )}
                    </div>
                    <p className="mt-3 text-lg font-extrabold text-slate-800">{b.name}</p>
                    <p className="text-sm text-slate-500">{b.description}</p>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setShowBadgeCelebration(false)}
                className="w-full mt-2 px-4 py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl font-bold text-sm active:scale-[0.98] transition-transform"
              >
                {t("common.continue", "Continuă")}
              </button>
            </div>
          </div>
        )}
        {/* Result card — accuracy ring + stars + points, on the game kit. */}
        <div className="px-4 pt-4 pb-24 max-w-lg mx-auto">
          <GameCard depth className="p-5 text-center">
            <div className="text-5xl mb-1">{passed ? "🎉" : "💪"}</div>
            <h2 className="text-2xl font-extrabold text-slate-800">
              {passed ? t("quiz.congratulations", "Felicitări!") : t("quiz.keep_trying", "Mai încearcă!")}
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              {t("quiz.answered_correctly", "Ai răspuns corect la")} {finalResult.correct_count}{" "}
              {t("quiz.out_of", "din")} {totalQuestions} {t("quiz.questions_lower", "întrebări")}
            </p>

            {/* Accuracy ring — question-based percentage, centered */}
            <div className="my-4 flex justify-center">
              <RingProgress pct={percentage} size={140} stroke={12} color={ringColor} track="#eef2f8">
                <div className="flex flex-col items-center">
                  <span className="text-4xl font-black text-slate-800 leading-none tabular-nums">{percentage}%</span>
                  <span className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-slate-400 mt-1">
                    {t("quiz.accuracy", "Acuratețe")}
                  </span>
                </div>
              </RingProgress>
            </div>

            {/* Stars — 1/2/3 by accuracy band */}
            <div className="flex justify-center gap-1.5 mb-4">
              {[0, 1, 2].map((i) => (
                <GameIcon key={i} name="star" className={`w-7 h-7 transition-all ${i < stars ? "" : "opacity-20 grayscale"}`} />
              ))}
            </div>

            {/* Correct / wrong cards */}
            <div className="grid grid-cols-2 gap-2.5 mb-3">
              <div className="bg-emerald-50 rounded-2xl p-3">
                <div className="text-2xl font-extrabold text-emerald-600 tabular-nums">{finalResult.correct_count}</div>
                <div className="text-[10.5px] font-bold uppercase tracking-wide text-emerald-600/70 mt-0.5">
                  {finalResult.correct_count === 1 ? t("quiz.correct_one", "Corect") : t("quiz.correct", "Corecte")}
                </div>
              </div>
              <div className="bg-red-50 rounded-2xl p-3">
                <div className="text-2xl font-extrabold text-red-500 tabular-nums">{finalResult.wrong_count}</div>
                <div className="text-[10.5px] font-bold uppercase tracking-wide text-red-500/70 mt-0.5">
                  {finalResult.wrong_count === 1 ? t("quiz.incorrect_one", "Greșit") : t("quiz.incorrect", "Greșite")}
                </div>
              </div>
            </div>

            {/* Newly earned badges */}
            {earnedBadges.length > 0 && (
              <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-100 rounded-2xl p-3 mb-3">
                <p className="text-[11px] text-violet-700 font-extrabold uppercase tracking-wider mb-2 text-center">
                  🎉 {t("quiz.new_badges_earned", "Insigne noi câștigate!")}
                </p>
                <div className="space-y-2">
                  {earnedBadges.map((b) => (
                    <div key={b.id} className="flex items-center gap-3 bg-white/80 rounded-xl p-2.5 border border-violet-100">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0 overflow-hidden" style={{ backgroundColor: `${b.color}20`, color: b.color }}>
                        {b.icon_image_url ? (
                          <img src={b.icon_image_url} alt={b.name} className="w-full h-full object-contain p-1" />
                        ) : (
                          b.icon_emoji || "🏆"
                        )}
                      </div>
                      <div className="min-w-0 flex-1 text-left">
                        <p className="font-bold text-slate-800 text-sm truncate">{b.name}</p>
                        <p className="text-xs text-slate-500 truncate">{b.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Points summary — now shows the points earned from correct answers */}
            {(grossEarned > 0 || deducted > 0 || awarded > 0) && (
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3 mb-3">
                <div className="flex items-center justify-center gap-1.5 mb-2">
                  <GameIcon name="star" className="w-4 h-4" />
                  <span className="text-amber-800 font-extrabold text-xs uppercase tracking-wide">{t("quiz.points_summary", "Rezumat Puncte")}</span>
                </div>
                <div className="space-y-1.5 text-sm">
                  {baseFromCorrect > 0 && (
                    <div className="flex items-center justify-between px-2.5 py-1.5 bg-white/70 rounded-lg">
                      <span className="text-slate-600 font-semibold">{t("quiz.points_from_correct", "Din răspunsuri corecte")}</span>
                      <span className="text-emerald-600 font-extrabold">+{baseFromCorrect}</span>
                    </div>
                  )}
                  {speedBonus > 0 && (
                    <div className="flex items-center justify-between px-2.5 py-1.5 bg-white/70 rounded-lg">
                      <span className="text-slate-600 font-semibold flex items-center gap-1">
                        <GameIcon name="bolt" className="w-3.5 h-3.5" />
                        {t("quiz.speed_bonus", "Bonus viteză")}
                      </span>
                      <span className="text-indigo-600 font-extrabold">+{speedBonus}</span>
                    </div>
                  )}
                  {finalResult.is_perfect_score && (
                    <div className="flex items-center justify-between px-2.5 py-1.5 bg-yellow-100/70 rounded-lg">
                      <span className="text-yellow-700 font-semibold">{t("quiz.perfect_score_bonus", "Bonus scor perfect!")}</span>
                      {perfectBonus > 0 ? (
                        <span className="text-yellow-700 font-extrabold">+{perfectBonus}</span>
                      ) : (
                        <span>🌟</span>
                      )}
                    </div>
                  )}

                  {/* Retake — spell out the credit concretely: previous
                      best, this run, and whether (and by how much) you
                      beat it. This is what actually gets credited. */}
                  {isRetake && (
                    <div className="rounded-lg bg-white/70 px-2.5 py-2 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">{t("quiz.prev_best_to_beat", "Scorul tău anterior de bătut")}</span>
                        <span className="text-slate-700 font-bold">{prevBest}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">{t("quiz.your_score_now", "Scorul tău acum")}</span>
                        <span className="text-slate-700 font-bold">{grossEarned}</span>
                      </div>
                      {beatBy > 0 ? (
                        <div className="flex items-center justify-between">
                          <span className="text-emerald-700 font-semibold">{t("quiz.beat_by", "Ai depășit cu")}</span>
                          <span className="text-emerald-600 font-extrabold">+{beatBy}</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-red-600 font-semibold">{t("quiz.not_beaten", "Nu ai depășit scorul anterior")}</span>
                          <span className="text-red-600 font-extrabold">+0</span>
                        </div>
                      )}
                    </div>
                  )}

                  {deducted > 0 && (
                    <div className="flex items-center justify-between px-2.5 py-1.5 bg-red-50 rounded-lg">
                      <span className="text-red-600 font-semibold">{t("quiz.retake_penalty", "Cost reluare")}</span>
                      <span className="text-red-600 font-extrabold">-{deducted}</span>
                    </div>
                  )}
                  {finalResult.net_points !== undefined && (
                    <div className="flex items-center justify-between px-2.5 py-2 bg-amber-100/80 rounded-lg border-t border-amber-200 mt-1">
                      <span className="text-amber-900 font-extrabold">
                        {(finalResult.net_points ?? 0) >= 0
                          ? t("quiz.net_added", "Adăugat în cont")
                          : t("quiz.net_removed", "Scăzut din cont")}
                      </span>
                      <span className={`font-black text-base ${(finalResult.net_points ?? 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {(finalResult.net_points ?? 0) >= 0 ? "+" : "−"}{Math.abs(finalResult.net_points ?? 0)} {t("quiz.points", "puncte")}
                      </span>
                    </div>
                  )}
                  {isRetake && (
                    <p className="text-[11px] text-slate-400 px-1 pt-0.5 leading-snug text-left">
                      {beatBy > 0
                        ? t("quiz.retake_credit_note", "La reluare se adaugă în cont doar progresul peste scorul tău anterior (plus bonusul de scor perfect, dacă e cazul), din care se scade costul reluării.")
                        : t("quiz.retake_no_improve_note2", "La reluare se adaugă doar progresul peste scorul tău anterior. Nu l-ai depășit, deci nu s-au adăugat puncte noi — dar costul reluării tot se scade.")}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Out of lives note */}
            {finalResult.out_of_lives && (
              <div className="flex items-center justify-center gap-2 text-sm font-bold text-red-500 mb-3">
                <LivesBadge lives={0} max={livesInfo?.max ?? 5} /> {t("quiz.out_of_lives", "Ai rămas fără vieți")}
              </div>
            )}

            {/* XP + level-up */}
            {finalResult.level_after && (
              <div className={`rounded-2xl p-3 mb-3 border ${finalResult.leveled_up ? "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200" : "bg-slate-50 border-slate-100"}`}>
                {finalResult.leveled_up && (
                  <p className="text-center text-sm font-extrabold text-amber-700 mb-2">
                    🎉 {t("quiz.level_up", "Level up! Ai ajuns la Nivelul")} {finalResult.level_after.level}
                  </p>
                )}
                <LevelBar
                  level={finalResult.level_after.level}
                  pct={finalResult.level_after.progress_pct}
                  title={finalResult.level_after.title}
                  xpInto={finalResult.level_after.xp_into_level}
                  xpFor={finalResult.level_after.xp_for_next}
                  className="w-full text-slate-700"
                />
              </div>
            )}

            {/* Next-quiz suggestion — the next step in the category order. A
                plan-locked next quiz routes a Free athlete to the plans screen
                instead of starting a paid quiz. */}
            {nextQuiz && (
              <button
                onClick={() =>
                  nextQuiz.plan_locked
                    ? navigate("/app/plans")
                    : navigate(`/quiz/${nextQuiz.id}`, { state: { from: backTarget } })
                }
                className={`w-full text-left mb-3 rounded-2xl p-3.5 border active:translate-y-[2px] transition-transform ${
                  nextQuiz.plan_locked
                    ? "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100"
                    : "bg-gradient-to-br from-indigo-50 to-blue-50 border-indigo-100"
                }`}
              >
                <div className={`text-[10px] font-extrabold uppercase tracking-[0.1em] ${nextQuiz.plan_locked ? "text-amber-600" : "text-indigo-500"}`}>
                  {t("quiz.next_quiz", "Următorul quiz")}
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  <div className={`w-12 h-12 rounded-xl grid place-items-center shrink-0 ${
                    nextQuiz.plan_locked
                      ? "bg-gradient-to-br from-amber-400 to-amber-500 shadow-[0_2px_0_#b45309]"
                      : "bg-gradient-to-br from-indigo-500 to-blue-600 shadow-[0_2px_0_#3730a3]"
                  }`}>
                    <GameIcon name={nextQuiz.plan_locked ? "lock" : "bulb"} className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-extrabold text-slate-800 truncate">{nextQuiz.title}</div>
                    <div className="text-xs text-slate-500 truncate flex items-center gap-2">
                      {nextQuiz.plan_locked ? (
                        <span className="inline-flex items-center gap-1 text-amber-600 font-bold shrink-0">
                          <GameIcon name="lock" className="w-3 h-3" />
                          {t("categoryQuizzes.premiumOnly", "Doar cu abonament")}
                        </span>
                      ) : (
                        <>
                          <span className="truncate">{nextQuiz.topic}</span>
                          <span className="inline-flex items-center gap-1 text-amber-600 font-bold shrink-0">
                            <GameIcon name="star" className="w-3 h-3" />{t("quiz.points_plus_bonus", "{{points}} puncte + bonus", { points: nextQuiz.max_points ?? nextQuiz.reward_points })}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <GameIcon name={nextQuiz.plan_locked ? "star" : "bolt"} className="w-5 h-5 shrink-0" />
                </div>
              </button>
            )}

            <div className="flex gap-2.5">
              {/* Back to where the athlete came from (the category quiz list),
                  so they can pick the next quiz — not the app home. */}
              <GameButton variant="primary" onClick={handleBack} className="flex-1">
                {t("quiz.back_to_quizzes", "Înapoi la quiz-uri")}
              </GameButton>
              <GameButton variant="ghost" onClick={handleRetryQuiz} className="flex-1">
                {t("quiz.retry_quiz", "Reia testul")}
              </GameButton>
            </div>
          </GameCard>
        </div>
      </div>
    );
  }

  return null;
}
